import { Formik, FormikHelpers } from "formik";
import {
  useFormSubmit,
  ControlcenterMelcoModelsSupplierProduct,
  UseApiQueryKey,
} from "melco-shared-logic";
import { QueryObserverResult, useQueryClient } from "react-query";
import { SubmitAction } from "../../header/SavePublishHeader";
import { DirtyFormPrompt } from "../../form/DirtyFormPrompt";
import { useUpdateExistingSupplierProductDraft } from "../../../hooks/supplierproducts/useUpdateExistingSupplierProductDraft";
import { usePublishSupplierProduct } from "../../../hooks/supplierproducts/usePublishSupplierProduct";
import { DisplaySupplierProductEditForm } from "./DisplaySupplierProductEditForm";

type SupplierProductEditFormProps = {
  isSupplierProductDrawerOpen: boolean;
  closeDrawerAction: () => void;
  supplierProduct: ControlcenterMelcoModelsSupplierProduct;
  refetch: () => Promise<
    QueryObserverResult<ControlcenterMelcoModelsSupplierProduct, unknown>
  >;
  queryKey: UseApiQueryKey;
};

type SupplierProductEditFormValueType = {
  id: string;
  name: string;
  code: string;
  submitAction: SubmitAction;
  publish_state: number;
  created_date: Date;
  publish_date: Date;
  supplier_id: String;
  supplier_name: String;
};

export const supplierProductToInitialValues = (
  supplierProduct: ControlcenterMelcoModelsSupplierProduct
) => {
  const {
    id,
    name,
    supplier_id,
    code,
    supplier_name,
    publish_state,
    publish_date,
    created_date,
  } = supplierProduct;

  const initialValues: SupplierProductEditFormValueType = {
    id: id!,
    name: name ?? "",
    code: code ?? "",
    publish_state: publish_state,
    created_date: created_date!,
    publish_date: publish_date!,
    submitAction: "save",
    supplier_id: supplier_id,
    supplier_name: supplier_name,
  };

  return initialValues;
};

export const SupplierProductEditForm: React.FC<
  SupplierProductEditFormProps
> = ({ queryKey, supplierProduct, refetch }) => {
  const queryClient = useQueryClient();

  // const { id } = supplierProduct;

  // const queryKey = ["supplierProduct", id, ALL_PUBLISHED_STATES];
  const updateExistingDraft = useUpdateExistingSupplierProductDraft();
  const publishSupplierProduct = usePublishSupplierProduct();

  const onSubmit = async (
    values: SupplierProductEditFormValueType,
    formikHelpers: FormikHelpers<SupplierProductEditFormValueType>
  ) => {
    const { submitAction } = values;

    if (submitAction === "publish") {
      await onPublish(values, formikHelpers, { isManuallyResettingForm: true });
    } else {
      await onSave(values, formikHelpers, { isManuallyResettingForm: true });
    }
  };

  const handleSaveAndPublish = async (
    values: SupplierProductEditFormValueType,
    formikHelpers: FormikHelpers<SupplierProductEditFormValueType>,
    options: {
      publish: boolean;
    }
  ) => {
    const { publish } = options;
    const { name, code, id } = values;

    let newEntity = await updateExistingDraft({
      id: id,
      name: name,
      code: code,
    });

    if (publish) {
      newEntity = await publishSupplierProduct({
        id: id!,
      });
    }

    // update ui with new data
    formikHelpers.resetForm({
      values: supplierProductToInitialValues(newEntity!),
    });
    queryClient.setQueryData(queryKey, newEntity);

    return {
      successMessages: [
        {
          message: "default",
        },
      ],
    };
  };

  const formSubmitWithPublish = (publish: boolean) => {
    return [
      async (
        values: SupplierProductEditFormValueType,
        formikHelpers: FormikHelpers<SupplierProductEditFormValueType>
      ) => {
        return handleSaveAndPublish(values, formikHelpers, {
          publish,
        });
      },
      {
        translationPrefix: [
          "supplierproducts.actions",
          publish ? "publish" : "save",
        ].join("."),
        onError: () => {
          // refetch entity from server
          queryClient.invalidateQueries(queryKey);
        },
      },
    ] as const;
  };

  const onSave = useFormSubmit(...formSubmitWithPublish(false));
  const onPublish = useFormSubmit(...formSubmitWithPublish(true));

  const initialValues = supplierProductToInitialValues(supplierProduct);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnBlur={false}
      validateOnChange={false}
    >
      <DirtyFormPrompt>
        <DisplaySupplierProductEditForm
          supplierProduct={supplierProduct}
          refetch={refetch}
        />
      </DirtyFormPrompt>
    </Formik>
  );
};
